import { useEffect, useMemo, useState } from 'react';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid2, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { CurrencyInputField, DateField, InputField } from 'components/form';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { CoverageStatusLabels, InsuranceBrokerForm } from '../../types';
import { formatPhoneInput } from 'formatters';
import { Reference, ReferenceType } from '@trnsact/trnsact-shared-types';
import { Q_CREDIT_APP_REFERENCES, QUERY_ACTIVE_OCA_TEMPLATE, UPDATE_CREDIT_APPLICATION_V2_QUERY } from '../../api';
import { insuranceActions, insuranceSelector } from '../../model';
import { useStepperState } from '../../hooks/useStepperState';
import { getInsuranceSectionIdx } from '../../lib';
import { coverageStatusConfig } from '../../constants';

export const InsuranceAddEditBrokerDialog = () => {
  const dispatch = useDispatch();

  const creditApp = useSelector((state: any) => state.creditApp);
  const vendorProfile = useSelector((state: any) => state.vp);
  const insuranceBroker = useSelector(insuranceSelector.insuranceBroker);
  const insuranceCertificate = useSelector(insuranceSelector.insuranceCertificate);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { isOpen, handleClose } = useModal(ModalsKeys.InsuranceAddEditBrokerDialog);

  const { showNotification } = useNotifications();
  const { stepperCompleteAgentInformationNeeded, stepperCompleteVerifyCoverage } = useStepperState();

  const [getCreditApp] = useLazyQuery(Q_CREDIT_APP_REFERENCES);

  const [getActiveOcaTemplate] = useLazyQuery(QUERY_ACTIVE_OCA_TEMPLATE, {
    variables: {
      vendorProfileId: vendorProfile.id,
      isActive: true,
    },
    fetchPolicy: 'no-cache',
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useFormContext<InsuranceBrokerForm>();

  const toDateOrNull = (v?: string | null) => {
    if (!v) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const emptyDefaults: InsuranceBrokerForm = useMemo(
    () => ({
      companyName: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      contactFax: '',
      renewalDate: null as any,
      physicalDamageDeduction: null as any,
      liabilityCoverage: null as any,
      productType: '',
    }),
    []
  );

  // ✅ Prefill on open
  useEffect(() => {
    if (!isOpen) return;

    if (insuranceBroker) {
      reset({
        companyName: insuranceBroker.companyName ?? '',
        contactName: insuranceBroker.contactName ?? '',
        contactEmail: insuranceBroker.contactEmail ?? '',
        contactPhone: insuranceBroker.contactPhone ?? '',
        contactFax: insuranceBroker.contactFax ?? '',
        renewalDate: toDateOrNull(insuranceBroker.renewalDate) as any,

        physicalDamageDeduction: (insuranceBroker.physicalDamageDeduction as any) ?? null,
        liabilityCoverage: (insuranceBroker.liabilityCoverage as any) ?? null,
        productType: insuranceBroker.productType ?? '',
      });
    } else {
      reset(emptyDefaults);
    }
  }, [isOpen, insuranceBroker, reset, emptyDefaults]);

  const [updateCreditAppRequest] = useMutation(UPDATE_CREDIT_APPLICATION_V2_QUERY, {
    onCompleted: async () => {
      const updatedCreditApp = await getCreditApp({ variables: { id: creditApp.id } });
      const updatedInsuranceBroker = updatedCreditApp?.data?.creditApplication.references?.find(
        (r: Reference) => r.referenceType === ReferenceType.Insurance
      );

      dispatch(insuranceActions.setInsuranceBroker(updatedInsuranceBroker));
      showNotification('Insurance Agent details have been saved');

      stepperCompleteAgentInformationNeeded();
      dispatch(insuranceActions.setCoverageStatusConfig(coverageStatusConfig[CoverageStatusLabels.InsuredUnverified]));

      if (insuranceCertificate) {
        stepperCompleteVerifyCoverage();
        dispatch(insuranceActions.setCoverageStatusConfig(coverageStatusConfig[CoverageStatusLabels.InsuredVerified]));
      }

      handleClose();
    },
  });

  const handleSave = async (formData: InsuranceBrokerForm) => {
    try {
      setIsLoading(true);

      const referenceInputData = Object.keys(formData).reduce((acc: any, key) => {
        const typedKey = key as keyof InsuranceBrokerForm;
        acc[key] = formData[typedKey] ? formData[typedKey] : null;
        return acc;
      }, {});

      const insuranceReferenceToSave = insuranceBroker
        ? {
            referenceId: insuranceBroker.referenceId,
            referenceIdx: insuranceBroker.referenceIdx,
            ...referenceInputData,
          }
        : {
            ...referenceInputData,
            referenceType: ReferenceType.Insurance,
          };

      if (!insuranceReferenceToSave.referenceIdx && insuranceReferenceToSave.referenceIdx !== 0) {
        const activeOcaTemplateData = await getActiveOcaTemplate();
        const referenceIdx = getInsuranceSectionIdx(activeOcaTemplateData.data.getOcaTemplatesByVendorProfile?.[0]);
        insuranceReferenceToSave.referenceIdx = referenceIdx;
      }

      await updateCreditAppRequest({
        variables: { creditAppId: creditApp.id, input: { references: [insuranceReferenceToSave] } },
      });
    } catch (error) {
      showNotification('Error', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>
        <Typography component="span" variant="h6">
          Add Agent &amp; Policy Details
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Grid2 container spacing={2}>
          <Grid2 container size={12}>
            <Grid2 size={6}>
              <InputField
                control={control}
                label="Insurance Company"
                name="companyName"
                rules={{ required: true }}
                inputProps={{
                  required: true,
                  error: !!errors.companyName,
                  helperText: errors.companyName && 'Required',
                }}
              />
            </Grid2>
          </Grid2>

          <Grid2 size={3}>
            <InputField control={control} label="Agent Name" name="contactName" />
          </Grid2>

          <Grid2 size={3}>
            <InputField
              control={control}
              label="Contact Email"
              name="contactEmail"
              rules={{ required: true }}
              inputProps={{
                required: true,
                error: !!errors.contactEmail,
                helperText: errors.contactEmail && 'Required',
              }}
            />
          </Grid2>

          <Grid2 size={3}>
            <InputField control={control} label="Phone Number" name="contactPhone" formatter={formatPhoneInput} />
          </Grid2>

          <Grid2 size={3}>
            <InputField control={control} label="Fax Number" name="contactFax" />
          </Grid2>

          <Grid2 size={3}>
            <DateField control={control} label="Policy Renewal Date" name="renewalDate" />
          </Grid2>

          <Grid2 size={3}>
            <CurrencyInputField control={control} label="Physical Damage Deductible" name="physicalDamageDeduction" />
          </Grid2>

          <Grid2 size={3}>
            <CurrencyInputField control={control} label="Liability Coverage" name="liabilityCoverage" />
          </Grid2>

          <Grid2 size={3}>
            <InputField control={control} label="Policy Type" name="productType" />
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit(handleSave)} variant="contained" loading={isLoading}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
